import { RealEstateOfficeViewModel } from '@models/RealEstateOffice';

import { TransactionCoordinatorSearchViewModel, TransactionCoordinatorViewModel } from '@models/TransactionCoordinator';
import { ContractSimpleViewModel } from '@models/Contract';

import { createReducer } from '@common/redux-utils';
import {
  GetTransactionCoordinatorFailureAction,
  GetTransactionCoordinatorStartAction,
  GetTransactionCoordinatorSuccessAction,
  SaveTransactionCoordinatorFailureAction,
  SaveTransactionCoordinatorStartAction,
  SaveTransactionCoordinatorSuccessAction,
  GetTransactionCoordinatorDetailStartAction,
  GetTransactionCoordinatorDetailSuccessAction,
  GetTransactionCoordinatorDetailFailureAction,
  GetContractsByCoordinatorStartAction,
  GetContractsByCoordinatorSuccessAction,
  GetContractsByCoordinatorFailureAction,
  TransactionCoordinatorActionType,
  TransactionCoordinatorSearchInitAction,
  TransactionCoordinatorSearchFailureAction,
  TransactionCoordinatorSearchSuccessAction
} from './transaction-coordinator.actions';
import { ClearTransactionCoordinatorStateAction } from './transaction-coordinator.actions';

// Defining the state of the page.
// Should be one per page.
// All properties should be readonly.
export interface TransactionCoordinatorState {
  readonly message?: string;
  readonly saving?: boolean;
  readonly realEstateOffice?: RealEstateOfficeViewModel;
  readonly query?: string | null;
  readonly transactionCoordinatorLoading?: boolean;
  readonly transactionCoordinatorPageCount?: number;
  readonly transactionCoordinatorPageNumber?: number;
  readonly transactionCoordinatorPageSize?: number;
  readonly transactionCoordinatorOrderBy?: string;
  readonly transactionCoordinators?: TransactionCoordinatorSearchViewModel[];
  readonly transactionCoordinatorVM?: TransactionCoordinatorViewModel;
  readonly contracts: Array<ContractSimpleViewModel>;
}

// This is the initial state of the page
const initialState: TransactionCoordinatorState = {
  message: 'Initialized',
  query: '',
  transactionCoordinatorLoading: false,
  transactionCoordinatorPageCount: 0,
  transactionCoordinatorPageNumber: 1,
  transactionCoordinators: [],
  transactionCoordinatorPageSize: 10,
  transactionCoordinatorOrderBy: 'id',
  transactionCoordinatorVM: {} as TransactionCoordinatorViewModel,
  contracts: []
};

/********** Save **********/
function handleSaveTransactionCoordinatorStart(
  state: TransactionCoordinatorState,
  action: SaveTransactionCoordinatorStartAction
): TransactionCoordinatorState {
  return {
    ...state,
    saving: true
  };
}

function handleSaveTransactionCoordinatorSuccess(
  state: TransactionCoordinatorState,
  action: SaveTransactionCoordinatorSuccessAction
): TransactionCoordinatorState {
  if (!action.currentId) {
    return {
      ...state,
      saving: false
    };
  }
  return {
    ...state,
    saving: false,
    message: 'SUCCESS - transaction coordinator saved - ' + action.currentId
  };
}

function handleSaveTransactionCoordinatorFailure(
  state: TransactionCoordinatorState,
  action: SaveTransactionCoordinatorFailureAction
): TransactionCoordinatorState {
  if (!action.errorMessage) {
    return {
      ...state,
      saving: false
    };
  }
  return {
    ...state,
    saving: false,
    message: 'ERROR - save transaction coordinator failed - ' + action.errorMessage
  };
}

/********** Get **********/
function handleGetTransactionCoordinatorStart(
  state: TransactionCoordinatorState,
  action: GetTransactionCoordinatorStartAction
): TransactionCoordinatorState {
  return state;
}

function handleGetTransactionCoordinatorSuccess(
  state: TransactionCoordinatorState,
  action: GetTransactionCoordinatorSuccessAction
): TransactionCoordinatorState {
  if (!action.txCoordinator && !action.realEstateOffice) {
    return state;
  }
  return {
    ...state,
    message: 'SUCCESS - get transaction coordinator done - ' + action.txCoordinator.id,
    transactionCoordinatorVM: action.txCoordinator,
    realEstateOffice: action.realEstateOffice
  };
}

function handleGetTransactionCoordinatorFailure(
  state: TransactionCoordinatorState,
  action: GetTransactionCoordinatorFailureAction
): TransactionCoordinatorState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    message: 'ERROR - get transaction coordinator failed - ' + action.errorMessage
  };
}

function handleGetTransactionCoordinatorDetailStart(
  state: TransactionCoordinatorState,
  action: GetTransactionCoordinatorDetailStartAction
): TransactionCoordinatorState {
  return {
    ...state,
    message: action.statusMessage
  };
}

function handleGetTransactionCoordinatorDetailSuccess(
  state: TransactionCoordinatorState,
  action: GetTransactionCoordinatorDetailSuccessAction
): TransactionCoordinatorState {
  return {
    ...state,
    transactionCoordinatorVM: action.transactionCoordinatorVM,
    message: action.statusMessage
  };
}

function handleGetTransactionCoordinatorDetailFailure(
  state: TransactionCoordinatorState,
  action: GetTransactionCoordinatorDetailFailureAction
): TransactionCoordinatorState {
  return {
    ...state,
    message: action.errorMessage
  };
}

function handleGetContractsByCoordinatorStart(
  state: TransactionCoordinatorState,
  action: GetContractsByCoordinatorStartAction
): TransactionCoordinatorState {
  return {
    ...state,
    message: ''
  };
}

function handleGetContractsByOfficeSuccess(
  state: TransactionCoordinatorState,
  action: GetContractsByCoordinatorSuccessAction
): TransactionCoordinatorState {
  return {
    ...state,
    message: action.statusMessage,
    contracts: action.result.results
  };
}

function handleGetContractsByOfficeFailure(
  state: TransactionCoordinatorState,
  action: GetContractsByCoordinatorFailureAction
): TransactionCoordinatorState {
  return {
    ...state,
    message: action.errorMessage
  };
}

/********** Search **********/
function handleTransactionCoordinatorSearchInit(
  state: TransactionCoordinatorState,
  action: TransactionCoordinatorSearchInitAction
): TransactionCoordinatorState {
  return {
    ...state,
    message: 'Search Init',
    query: action.query,
    transactionCoordinatorLoading: true
  };
}
function handleTransactionCoordinatorSearchFailure(
  state: TransactionCoordinatorState,
  action: TransactionCoordinatorSearchFailureAction
): TransactionCoordinatorState {
  return {
    ...state,
    message: action.errorMessage,
    transactionCoordinatorLoading: false
  };
}
function handleTransactionCoordinatorSearchSuccess(
  state: TransactionCoordinatorState,
  action: TransactionCoordinatorSearchSuccessAction
): TransactionCoordinatorState {
  return {
    ...state,
    message: 'Search Success!',
    transactionCoordinatorLoading: false,
    transactionCoordinatorPageCount: Math.ceil(action.result.totalResults / action.result.pageSize),
    transactionCoordinatorPageNumber: action.pageNumber,
    transactionCoordinatorPageSize: action.pageSize,
    transactionCoordinatorOrderBy: action.orderBy,
    transactionCoordinators: action.result.results
  };
}

function handleClearTransactionCoordinatorState(
  state: TransactionCoordinatorState,
  action: ClearTransactionCoordinatorStateAction
): TransactionCoordinatorState {
  return {
    ...initialState
  };
}

// This creates the reducer as redux wants it w/ a helper function
// There should an entry for each action type
export const TransactionCoordinatorReducer = createReducer(initialState, {
  /********** Save **********/
  [TransactionCoordinatorActionType.SaveTransactionCoordinatorStart]: handleSaveTransactionCoordinatorStart,
  [TransactionCoordinatorActionType.SaveTransactionCoordinatorSuccess]: handleSaveTransactionCoordinatorSuccess,
  [TransactionCoordinatorActionType.SaveTransactionCoordinatorFailure]: handleSaveTransactionCoordinatorFailure,

  /********** Get **********/
  [TransactionCoordinatorActionType.GetTransactionCoordinatorStart]: handleGetTransactionCoordinatorStart,
  [TransactionCoordinatorActionType.GetTransactionCoordinatorSuccess]: handleGetTransactionCoordinatorSuccess,
  [TransactionCoordinatorActionType.GetTransactionCoordinatorFailure]: handleGetTransactionCoordinatorFailure,

  [TransactionCoordinatorActionType.GetTransactionCoordinatorDetailStart]: handleGetTransactionCoordinatorDetailStart,
  [TransactionCoordinatorActionType.GetTransactionCoordinatorDetailSuccess]: handleGetTransactionCoordinatorDetailSuccess,
  [TransactionCoordinatorActionType.GetTransactionCoordinatorDetailFailure]: handleGetTransactionCoordinatorDetailFailure,

  [TransactionCoordinatorActionType.GetContractsByCoordinatorStart]: handleGetContractsByCoordinatorStart,
  [TransactionCoordinatorActionType.GetContractsByCoordinatorSuccess]: handleGetContractsByOfficeSuccess,
  [TransactionCoordinatorActionType.GetContractsByCoordinatorFailure]: handleGetContractsByOfficeFailure,

  /********** Search **********/
  [TransactionCoordinatorActionType.TransactionCoordinatorSearchInit]: handleTransactionCoordinatorSearchInit,
  [TransactionCoordinatorActionType.TransactionCoordinatorSearchFailure]: handleTransactionCoordinatorSearchFailure,
  [TransactionCoordinatorActionType.TransactionCoordinatorSearchSuccess]: handleTransactionCoordinatorSearchSuccess,

  [TransactionCoordinatorActionType.ClearTransactionCoordinatorState]: handleClearTransactionCoordinatorState
});



// WEBPACK FOOTER //
// ./src/transaction-coordinator/transaction-coordinator.reducer.ts